import React, { useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

const TradeCalendar = ({ data = [], userSettings }) => {
  const [currentDate, setCurrentDate] = useState(new Date());

  // Calendar helper functions
  const daysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const startDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  // Navigation functions
  const previousMonth = () => {
    setCurrentDate(
      new Date(currentDate.getFullYear(), currentDate.getMonth() - 1)
    );
  };

  const nextMonth = () => {
    setCurrentDate(
      new Date(currentDate.getFullYear(), currentDate.getMonth() + 1)
    );
  };

  // Format dates
  const formatDate = (date) => {
    return date.toLocaleDateString("default", {
      month: "long",
      year: "numeric",
    });
  };

  // Calculate adjusted P&L with commissions
  const calculateAdjustedPnL = (trade) => {
    if (!userSettings?.useCommissions) return trade.pnl;

    const commissionCost = userSettings.commissionValue * trade.quantity * 2;
    return trade.pnl - commissionCost;
  };

  // Get trade data for a specific day
  const getDayData = (day) => {
    const dateKey = `${currentDate.getFullYear()}-${String(
      currentDate.getMonth() + 1
    ).padStart(2, "0")}-${String(day).padStart(2, "0")}`;

    // Filter trades for this day
    const dayTrades = data.filter((trade) => {
      const tradeDate = new Date(trade.date);
      return tradeDate.toISOString().split("T")[0] === dateKey;
    });

    if (dayTrades.length === 0) return null;

    // Calculate total P&L with commissions for the day
    let totalPnL = dayTrades.reduce(
      (sum, trade) => sum + calculateAdjustedPnL(trade),
      0
    );

    // Apply account multiplier for funded accounts if enabled
    if (userSettings?.useFundedBalance && userSettings.fundedAccountCount > 1) {
      totalPnL *= userSettings.fundedAccountCount;
    }

    return {
      trades: dayTrades.length,
      pnl: totalPnL,
      // If using multiple accounts, show additional info
      accountMultiplier:
        userSettings?.useFundedBalance && userSettings.fundedAccountCount > 1
          ? userSettings.fundedAccountCount
          : null,
    };
  };

  // Render calendar grid
  const renderCalendar = () => {
    const days = daysInMonth(currentDate);
    const startDay = startDayOfMonth(currentDate);
    const weeks = Math.ceil((days + startDay) / 7);
    const calendar = [];

    // Day names row
    const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

    let dayCounter = 1;

    for (let week = 0; week < weeks; week++) {
      const weekDays = [];
      for (let dayOfWeek = 0; dayOfWeek < 7; dayOfWeek++) {
        if ((week === 0 && dayOfWeek < startDay) || dayCounter > days) {
          weekDays.push(
            <td
              key={`empty-${week}-${dayOfWeek}`}
              className="calendar-day empty"
            ></td>
          );
        } else {
          const dayData = getDayData(dayCounter);
          const dayClasses = ["calendar-day"];

          if (dayData) {
            dayClasses.push(dayData.pnl >= 0 ? "profit" : "loss");
          }

          weekDays.push(
            <td key={dayCounter} className={dayClasses.join(" ")}>
              <div className="day-content">
                <span className="day-number">{dayCounter}</span>
                {dayData && (
                  <div className="day-data">
                    <span className="trade-count">
                      {dayData.trades} trades
                      {dayData.accountMultiplier &&
                        ` × ${dayData.accountMultiplier}`}
                    </span>
                    <span
                      className={`day-pnl ${
                        dayData.pnl >= 0 ? "positive" : "negative"
                      }`}
                    >
                      ${Math.abs(dayData.pnl).toFixed(2)}
                    </span>
                  </div>
                )}
              </div>
            </td>
          );
          dayCounter++;
        }
      }
      calendar.push(<tr key={`week-${week}`}>{weekDays}</tr>);
    }

    return (
      <>
        <tr>
          {dayNames.map((day) => (
            <th key={day} className="day-name">
              {day}
            </th>
          ))}
        </tr>
        {calendar}
      </>
    );
  };

  return (
    <div className="trade-calendar">
      <div className="calendar-header">
        <button onClick={previousMonth}>
          <ChevronLeft size={20} />
        </button>
        <h2>{formatDate(currentDate)}</h2>
        <button onClick={nextMonth}>
          <ChevronRight size={20} />
        </button>
      </div>
      <table className="calendar-grid">
        <tbody>{renderCalendar()}</tbody>
      </table>
      {userSettings?.useFundedBalance &&
        userSettings.fundedAccountCount > 1 && (
          <div
            style={{
              fontSize: "0.8rem",
              color: "var(--text-secondary)",
              textAlign: "center",
              marginTop: "0.5rem",
            }}
          >
            P&L multiplied across {userSettings.fundedAccountCount} accounts
          </div>
        )}
    </div>
  );
};

export default TradeCalendar;
